import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { convertToUUID } from 'uuid-encoding';
import { useVisitorCustomFieldConfigs } from 'domains/visitorManagement/hooks/useVisitorCustomFieldConfigs';
import type { VisitorConfigurationTabProps } from '../../types';
import { mapQueryConfigsToDetailRows } from './mapCustomFieldConfigRow';
import { filterCustomFieldDetailRowsBySearch } from './searchFilter';

export function useCustomFieldsTabModel({
  channel,
}: Pick<VisitorConfigurationTabProps, 'channel'>) {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState('');
  const organizationId = channel?._id ? convertToUUID(channel._id) : null;
  const {
    data: customFieldConfigs,
    loading: configsLoading,
    error: configsError,
    called: configsCalled,
    fetch: fetchConfigs,
  } = useVisitorCustomFieldConfigs();

  useEffect(() => {
    if (!organizationId) {
      return;
    }

    fetchConfigs(organizationId);
  }, [organizationId, fetchConfigs]);

  const detailRows = useMemo(
    () => mapQueryConfigsToDetailRows(customFieldConfigs ?? []),
    [customFieldConfigs]
  );

  const filteredRows = useMemo(
    () => filterCustomFieldDetailRowsBySearch(detailRows, searchValue, t),
    [detailRows, searchValue, t]
  );

  const isListLoading = detailRows.length === 0 && configsLoading;

  return {
    t,
    searchValue,
    setSearchValue,
    filteredRows,
    isListLoading,
    configsCalled,
    configsError,
  };
}
